'use client';

import React, { useState, createContext, useContext, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';
import Dispatcher from '@dealroadshow/json-rpc-dispatcher';
import ErrorCodeHelper from '@dealroadshow/error-codes';
import { useTraceFiltersContext } from '@/creditflowresearch/application/Trace/TraceFilters/TraceFiltersContext';
import TraceRepository from '@/creditflowresearch/infrastructure/repository/TraceRepository';
import { getContainer } from '@framework/di/getContainer';
import { ITradesCollection } from '@/creditflowresearch/domain/vo/Trace/ITraceData';
import { useTraceAssetClassesContext } from '@/creditflowresearch/application/Trace/TraceAssetClassesContext';
import { useFiltersContext } from '@/creditflowresearch/application/FiltersContext';
import { initialTraceData } from '@/creditflowresearch/application/Trace/constants';
import { useSessionContext } from '@users/session/SessionContext';
import useGetTracePayload from '@/creditflowresearch/application/Trace/useGetTracePayload';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { IMarketTradesPayload } from '@/creditflowresearch/domain/vo/Trace/IMarketTradesPayload';

export const useTraceTable = () => {
  const container = getContainer();
  const traceRepository = container.get(TraceRepository);
  const [traceData, setTraceData] = useState<ITradesCollection>(initialTraceData.trades);
  const [isTraceFetching, setIsTraceFetching] = useState(false);
  const { filtersPayload: filter } = useTraceFiltersContext();
  const { currentAssetClass } = useTraceAssetClassesContext();
  const { params } = useFiltersContext();
  const { logout } = useSessionContext();
  const { isPro } = useUserPlanContext();

  const { getPayload } = useGetTracePayload();

  useEffect(() => {
    getTrades(getPayload({}));
  }, [params.sortBy, params.sortOrder, params.page, params.perPage, currentAssetClass, filter]);

  const getTrades = async (payload: Partial<IMarketTradesPayload>) => {
    if (!isPro) return;

    setIsTraceFetching(true);
    try {
      // @ts-ignore TODO: incorrect type of payload with correct repository type
      const response = await traceRepository.getMarketTrades(payload);
      setTraceData(response.trades);
    } catch (e) {
      if (e.code === ErrorCodeHelper.getCodeByName('FINSIGHT_CONTACT_SUBSCRIPTION_REQUIRED')) {
        logout();
      }
      if (Dispatcher.isAbortError(e)) {
        return;
      }
    }

    setIsTraceFetching(false);
  };

  const debouncedGetTrades = useCallback(debounce(getTrades, 500), []);

  return {
    isTraceFetching,
    getTrades,
    setTraceData,
    traceData,
    debouncedGetTrades,
  };
};

type TraceTableContextType = ReturnType<typeof useTraceTable>;

export const TraceTableContext = createContext<TraceTableContextType>(null);

export const useTraceTableContext = () => {
  const context = useContext(TraceTableContext);
  if (!context) {
    throw new Error('useTraceTableContext must be used within the TraceTableContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const TraceTableContextProvider = ({ children }: IProps) => (
  <TraceTableContext.Provider value={ useTraceTable() }>
    { children }
  </TraceTableContext.Provider>
);

export default TraceTableContextProvider;
